import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiPatch, apiPost } from '../../../lib/apiClient.js';
import { useApiGet } from '../../../lib/useApiGet.js';
import { JalaliDateTimeInput } from '../../../lib/date/JalaliDateTimeInput.js';
import { relativeWhen, wallClockToIso } from '../../../lib/date/relativeWhen.js';
import { toEnglishDigits } from '../../../lib/phoneNumber.js';
import { JalaliDateInput } from '../../../lib/date/JalaliDateInput.js';
import { formatRialFa, parseRialInput } from '../../../lib/finance-display.js';
import { useAmountDisplay } from '../../../lib/tenantSettings.js';
import type { MasterDataListResponse } from '../../../lib/types/masterData.js';
import { SidePanel } from '../../../components/list/SidePanel.js';
import type {
  LeadDetail,
  LeadKind,
  LeadOptionsResponse,
  LeadOwnersResponse,
  LeadPriority,
  LeadRow,
} from '../../../lib/types/crm.js';
import { detailValue, fieldErrorsFrom, normalizeMobileInput } from './leadUi.js';
import { LabelField } from '../LabelField.js';

type AltKind = '' | 'email' | 'messenger' | 'other';

interface FormState {
  leadKind: LeadKind;
  personFirstName: string;
  personLastName: string;
  organizationName: string;
  mobile: string;
  phone: string;
  altContactKind: AltKind;
  altContactValue: string;
  needSubject: string;
  sourceLabel: string;
  sourceDetail: string;
  ownerMemberRef: string;
  nextFollowUp: string;
  initialNote: string;
  partyRef: string;
  stageRef: string;
  amountRial: string;
  expectedCloseDate: string;
  priority: LeadPriority;
  needDescription: string;
  nextActionTitle: string;
  nextActionDueAt: string;
  products: { readonly ref: string; readonly name: string }[];
  /** Label ids (from /crm/labels), picked or created in place. */
  labelIds: string[];
}

const EMPTY: FormState = {
  leadKind: 'person',
  personFirstName: '',
  personLastName: '',
  organizationName: '',
  mobile: '',
  phone: '',
  altContactKind: '',
  altContactValue: '',
  needSubject: '',
  sourceLabel: '',
  sourceDetail: '',
  ownerMemberRef: '',
  nextFollowUp: '',
  initialNote: '',
  partyRef: '',
  stageRef: '',
  amountRial: '',
  expectedCloseDate: '',
  priority: 'normal',
  needDescription: '',
  nextActionTitle: '',
  nextActionDueAt: '',
  products: [],
  labelIds: [],
};

/** ISO instant → the `YYYY-MM-DDTHH:mm` wall-clock value the date-time input holds. */
function wallClockOf(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function fromLead(lead: LeadDetail): FormState {
  return {
    ...EMPTY,
    leadKind: lead.leadKind ?? 'organization',
    personFirstName: lead.personFirstName ?? '',
    personLastName: lead.personLastName ?? '',
    organizationName: lead.organizationName ?? (lead.leadKind === null ? lead.title : ''),
    needSubject: lead.needSubject ?? '',
    sourceLabel: lead.sourceLabel ?? '',
    sourceDetail: lead.sourceDetail ?? '',
    ownerMemberRef: lead.owner?.memberRef ?? '',
    partyRef: lead.deal.customer?.ref ?? '',
    stageRef: lead.deal.stage?.ref ?? '',
    amountRial: lead.deal.amountRial === '0' ? '' : lead.deal.amountRial,
    expectedCloseDate: lead.deal.expectedCloseDate ?? '',
    priority: lead.deal.priority,
    needDescription: lead.deal.needDescription ?? '',
    nextActionTitle: lead.deal.ownNextAction?.title ?? '',
    nextActionDueAt: wallClockOf(lead.deal.ownNextAction?.dueAt),
    products: [...lead.products],
    labelIds: (lead.labels ?? []).map((label) => label.labelId),
  };
}

function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label htmlFor={id} style={{ fontSize: 13, fontWeight: 500, color: 'var(--sk-fg)' }}>
        {label}
        {required ? <span style={{ color: 'var(--sk-danger)' }}> *</span> : null}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} role="alert" style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
          {error}
        </span>
      ) : hint ? (
        <span style={{ fontSize: 12, lineHeight: 1.7, color: 'var(--sk-fg-subtle)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

function inputProps(id: string, error: string | undefined) {
  return {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-error` : undefined,
    style: error
      ? {
          borderColor: 'var(--sk-danger)',
          boxShadow: '0 0 0 3px color-mix(in oklch, var(--sk-danger), transparent 82%)',
        }
      : undefined,
  } as const;
}

const grid2: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 14,
};

/**
 * «سرنخ جدید» / «ویرایش سرنخ» — the Sales CRM lead form (§02 LEAD FORM),
 * extended per the product spec: person/organisation, separate names,
 * normalised mobile + landline + one other channel, need, source (+detail
 * for «سایر»), owner from the members who can read leads, an optional
 * follow-up that becomes a real activity, and an initial note that lands
 * in the timeline. Errors come back field-by-field from the server and sit
 * under their field; typed data is kept on any failure.
 */
export function LeadFormPanel({
  base,
  open,
  editing,
  onClose,
  onSaved,
  onOpenLead,
  presetPartyRef,
  presetMobile,
}: {
  base: string;
  /** Starts a new lead with this mobile/phone number filled in (e.g. an unknown caller). */
  presetMobile?: string | undefined;
  open: boolean;
  /** Opens a new form already aimed at this customer (from Customer 360). */
  presetPartyRef?: string | undefined;
  editing: LeadDetail | null;
  onClose: () => void;
  onSaved: (lead: LeadRow, keepOpen: boolean) => void;
  onOpenLead: (ref: string) => void;
}) {
  const options = useApiGet<LeadOptionsResponse>(open ? `${base}/api/v1/crm/leads/options` : null, [
    open,
  ]);
  const ownerDirectory = useApiGet<LeadOwnersResponse>(
    open && options.status === 'ready' && options.data.canAssignOwner
      ? `${base}/api/v1/crm/lead-owners`
      : null,
    [open, options.status === 'ready' ? options.data.canAssignOwner : false],
  );
  const { label: unitLabel } = useAmountDisplay();
  // A new deal is either a brand-new lead or one raised for an existing customer.
  const [target, setTarget] = useState<'new' | 'customer'>('new');
  const [productSearch, setProductSearch] = useState('');
  const customers = useApiGet<MasterDataListResponse>(
    open && !editing && target === 'customer'
      ? `${base}/api/v1/master-data/parties?role=customer&limit=200`
      : null,
    [open, editing, target],
  );
  const productList = useApiGet<MasterDataListResponse>(
    open
      ? `${base}/api/v1/master-data/products?status=active&limit=20&search=${encodeURIComponent(productSearch.trim())}`
      : null,
    [open, productSearch],
  );
  const [form, setForm] = useState<FormState>(EMPTY);
  const [initial, setInitial] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ ref: string; title: string; at: string } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const start = editing
      ? fromLead(editing)
      : presetPartyRef
        ? { ...EMPTY, partyRef: presetPartyRef }
        : presetMobile
          ? { ...EMPTY, mobile: presetMobile }
          : EMPTY;
    setForm(start);
    setInitial(start);
    setErrors({});
    setBanner(null);
    setDuplicate(null);
    setTarget(
      editing
        ? editing.leadKind === null && editing.deal.customer
          ? 'customer'
          : 'new'
        : presetPartyRef
          ? 'customer'
          : 'new',
    );
    setProductSearch('');
  }, [open, editing]);

  // The first stage of the default pipeline is where a new lead starts.
  useEffect(() => {
    if (options.status !== 'ready' || editing || form.stageRef) return;
    const pipeline = options.data.pipelines.find((p) => p.isDefault) ?? options.data.pipelines[0];
    const first = pipeline?.stages[0]?.ref;
    if (first) {
      setForm((f) => ({ ...f, stageRef: first }));
      setInitial((f) => ({ ...f, stageRef: first }));
    }
  }, [options, editing, form.stageRef]);

  // Owner defaults to the person filling the form (spec: «پیش‌فرض ثبت‌کننده»).
  useEffect(() => {
    if (
      options.status !== 'ready' ||
      !options.data.canAssignOwner ||
      editing ||
      form.ownerMemberRef
    )
      return;
    const me = options.data.owners.find((o) => o.isMe);
    if (me?.memberRef) {
      setForm((f) => ({ ...f, ownerMemberRef: me.memberRef ?? '' }));
      setInitial((f) => ({ ...f, ownerMemberRef: me.memberRef ?? '' }));
    }
  }, [options, editing, form.ownerMemberRef]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const sources = options.status === 'ready' ? options.data.sources : [];
  const selfOwnerRef =
    options.status === 'ready'
      ? (options.data.owners.find((owner) => owner.isMe)?.memberRef ?? null)
      : null;
  const needsDetail =
    options.status === 'ready' && options.data.sourcesRequiringDetail.includes(form.sourceLabel);

  /** Mirrors the server rules so obvious mistakes never need a round trip. */
  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!editing && target === 'customer') {
      if (!form.partyRef) next['partyRef'] = 'مشتری را انتخاب کنید.';
      if (!form.needSubject.trim()) next['needSubject'] = 'موضوع نیاز را بنویسید.';
      if (form.amountRial && !/^\d{1,18}$/.test(form.amountRial)) {
        next['amountRial'] = 'مبلغ باید عدد صحیح و نامنفی (ریال) باشد.';
      }
      if (form.nextActionDueAt && !form.nextActionTitle.trim()) {
        next['nextActionTitle'] = 'برای موعد اقدام بعدی، عنوان آن را بنویسید.';
      }
      return next;
    }
    if (form.leadKind === 'person') {
      if (!form.personFirstName.trim()) next['personFirstName'] = 'نام را وارد کنید.';
      if (!form.personLastName.trim()) next['personLastName'] = 'نام خانوادگی را وارد کنید.';
    } else if (!form.organizationName.trim()) {
      next['organizationName'] = 'نام مجموعه را وارد کنید.';
    }
    if (form.mobile.trim() && normalizeMobileInput(form.mobile) === null) {
      next['mobile'] = 'شمارهٔ همراه معتبر نیست.';
    }
    if (!form.mobile.trim() && !form.phone.trim() && !form.altContactValue.trim()) {
      next['mobile'] = 'دست‌کم یک راه ارتباطی (موبایل، تلفن ثابت یا راه دیگر) لازم است.';
    }
    if (form.altContactValue.trim() && !form.altContactKind) {
      next['altContactKind'] = 'نوع راه ارتباطی را انتخاب کنید.';
    }
    if (!form.needSubject.trim()) next['needSubject'] = 'موضوع نیاز را بنویسید.';
    if (!form.sourceLabel) next['sourceLabel'] = 'منبع را انتخاب کنید.';
    if (needsDetail && !form.sourceDetail.trim()) {
      next['sourceDetail'] = 'برای این منبع توضیح لازم است.';
    }
    if (form.nextFollowUp && wallClockToIso(form.nextFollowUp) === null) {
      next['nextFollowUpAt'] = 'زمان پیگیری معتبر نیست.';
    }
    if (form.amountRial && !/^\d{1,18}$/.test(form.amountRial)) {
      next['amountRial'] = 'مبلغ باید عدد صحیح و نامنفی (ریال) باشد.';
    }
    if (form.nextActionDueAt && !form.nextActionTitle.trim()) {
      next['nextActionTitle'] = 'برای موعد اقدام بعدی، عنوان آن را بنویسید.';
    }
    return next;
  }

  async function submit(keepOpen: boolean) {
    const clientErrors = validate();
    setErrors(clientErrors);
    setBanner(null);
    setDuplicate(null);
    if (Object.keys(clientErrors).length > 0) {
      setBanner('اطلاعات واردشده کامل نیست.');
      return;
    }
    const dealBody = {
      amountRial: form.amountRial || (editing ? '0' : undefined),
      expectedCloseDate: form.expectedCloseDate || (editing ? null : undefined),
      priority: form.priority,
      needDescription: form.needDescription.trim() || (editing ? '' : undefined),
      nextActionTitle: form.nextActionTitle.trim() || (editing ? '' : undefined),
      nextActionDueAt: form.nextActionDueAt
        ? (wallClockToIso(form.nextActionDueAt) ?? undefined)
        : editing
          ? null
          : undefined,
      productRefs: form.products.map((product) => product.ref),
      labelIds: form.labelIds,
    };
    const customerDeal = !editing && target === 'customer';
    const body = customerDeal
      ? {
          partyRef: form.partyRef,
          needSubject: form.needSubject.trim(),
          sourceLabel: form.sourceLabel || undefined,
          sourceDetail: form.sourceDetail.trim() || undefined,
          ownerMemberRef: form.ownerMemberRef || undefined,
          stageRef: form.stageRef || undefined,
          ...dealBody,
        }
      : {
          leadKind: form.leadKind,
          personFirstName: form.personFirstName.trim() || undefined,
          personLastName: form.personLastName.trim() || undefined,
          organizationName:
            form.leadKind === 'organization' ? form.organizationName.trim() : undefined,
          mobile: form.mobile.trim()
            ? (normalizeMobileInput(form.mobile) ?? form.mobile)
            : undefined,
          phone: toEnglishDigits(form.phone).trim() || undefined,
          altContactKind: form.altContactValue.trim()
            ? form.altContactKind || undefined
            : undefined,
          altContactValue: form.altContactValue.trim() || undefined,
          needSubject: form.needSubject.trim(),
          sourceLabel: form.sourceLabel,
          sourceDetail: form.sourceDetail.trim() || undefined,
          ownerMemberRef: form.ownerMemberRef || undefined,
          ...(editing ? {} : { stageRef: form.stageRef || undefined }),
          ...dealBody,
        };
    setSubmitting(true);
    try {
      const saved = editing
        ? await apiPatch<LeadRow>(`${base}/api/v1/crm/leads/${editing.ref}`, body)
        : await apiPost<LeadRow>(`${base}/api/v1/crm/leads`, {
            ...body,
            nextFollowUpAt: form.nextFollowUp ? wallClockToIso(form.nextFollowUp) : undefined,
            initialNote: form.initialNote.trim() || undefined,
          });
      if (keepOpen) {
        // «ثبت و افزودن بعدی» clears only after a success; owner/source stay.
        const next = {
          ...EMPTY,
          ownerMemberRef: form.ownerMemberRef,
          sourceLabel: form.sourceLabel,
          stageRef: form.stageRef,
          labelIds: form.labelIds,
        };
        setForm(next);
        setInitial(next);
        setBanner(null);
      }
      onSaved(saved, keepOpen);
    } catch (error) {
      setErrors(fieldErrorsFrom(error));
      const dupRef = detailValue(error, 'duplicateRef');
      if (dupRef) {
        setDuplicate({
          ref: dupRef,
          title: detailValue(error, 'duplicateTitle') ?? dupRef,
          at: detailValue(error, 'duplicateCreatedAt') ?? '',
        });
        setBanner(null);
      } else {
        setBanner(
          Object.keys(fieldErrorsFrom(error)).length > 0
            ? 'اطلاعات واردشده کامل نیست.'
            : 'ثبت انجام نشد. اطلاعات واردشده حفظ شده است؛ دوباره تلاش کنید.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const kindButton = (kind: LeadKind, label: string) => (
    <label
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 'var(--sk-r-md)',
        border: `1px solid ${form.leadKind === kind ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
        background:
          form.leadKind === kind
            ? 'color-mix(in oklch, var(--sk-brand-500), transparent 92%)'
            : 'var(--sk-bg-elev)',
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      <input
        type="radio"
        name="lead-kind"
        checked={form.leadKind === kind}
        onChange={() => set('leadKind', kind)}
      />
      {label}
    </label>
  );

  return (
    <SidePanel
      open={open}
      title={editing ? 'ویرایش سرنخ' : 'سرنخ جدید'}
      subtitle={
        editing
          ? editing.title
          : 'ورودی قیف فروش — شخص یا مجموعهٔ خریدار، یا معامله‌ای تازه برای مشتری موجود'
      }
      onClose={onClose}
      dirty={dirty}
      width={640}
      footer={
        <>
          <button
            type="button"
            className="sk-btn sk-btn--primary"
            disabled={submitting}
            onClick={() => void submit(false)}
          >
            {submitting ? 'در حال ثبت…' : editing ? 'ذخیرهٔ تغییرات' : 'ثبت سرنخ'}
          </button>
          {editing ? null : (
            <button
              type="button"
              className="sk-btn sk-btn--secondary"
              disabled={submitting}
              onClick={() => void submit(true)}
            >
              ثبت و افزودن بعدی
            </button>
          )}
          <button type="button" className="sk-btn sk-btn--ghost" onClick={onClose}>
            انصراف
          </button>
        </>
      }
    >
      {banner ? (
        <div
          role="alert"
          style={{
            padding: '12px 20px',
            background: 'var(--sk-danger-soft)',
            borderBottom: '1px solid var(--sk-divider)',
            fontSize: 13,
            color: 'var(--sk-fg)',
          }}
        >
          {banner}
        </div>
      ) : null}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit(false);
        }}
        style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        {!editing ? (
          <fieldset
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <legend style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              این معامله برای
            </legend>
            <div style={{ display: 'flex', gap: 10 }}>
              {(
                [
                  ['new', 'سرنخ جدید (هنوز مشتری نیست)'],
                  ['customer', 'مشتری موجود'],
                ] as const
              ).map(([value, text]) => (
                <label
                  key={value}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    borderRadius: 'var(--sk-r-md)',
                    border: `1px solid ${target === value ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                    background:
                      target === value
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 92%)'
                        : 'var(--sk-bg-elev)',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <input
                    type="radio"
                    name="lead-target"
                    checked={target === value}
                    onChange={() => {
                      setTarget(value);
                      setErrors({});
                    }}
                  />
                  {text}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {!editing && target === 'customer' ? (
          <Field
            id="lead-party"
            label="مشتری"
            required
            error={errors['partyRef']}
            hint="نام و راه‌های ارتباطی از خود مشتری خوانده می‌شود."
          >
            <select
              className="sk-select"
              value={form.partyRef}
              disabled={customers.status !== 'ready'}
              onChange={(e) => set('partyRef', e.target.value)}
              {...inputProps('lead-party', errors['partyRef'])}
            >
              <option value="">
                {customers.status === 'loading'
                  ? 'در حال دریافت مشتریان…'
                  : customers.status === 'error'
                    ? 'مشتریان قابل دریافت نیست'
                    : '— انتخاب مشتری —'}
              </option>
              {customers.status === 'ready'
                ? customers.data.items.map((party) => (
                    <option key={String(party['code'])} value={String(party['code'])}>
                      {String(party['name'] ?? party['code'])}
                    </option>
                  ))
                : null}
            </select>
          </Field>
        ) : (
          <>
            <fieldset
              style={{
                border: 0,
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <legend style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                نوع سرنخ <span style={{ color: 'var(--sk-danger)' }}>*</span>
              </legend>
              <div style={{ display: 'flex', gap: 10 }}>
                {kindButton('person', 'شخص')}
                {kindButton('organization', 'سازمان')}
              </div>
            </fieldset>

            {form.leadKind === 'organization' ? (
              <Field
                id="lead-org"
                label="نام مجموعه"
                required
                error={errors['organizationName']}
                hint="نام شرکت، فروشگاه یا سازمانی که خرید می‌کند."
              >
                <input
                  className="sk-input"
                  value={form.organizationName}
                  onChange={(e) => set('organizationName', e.target.value)}
                  {...inputProps('lead-org', errors['organizationName'])}
                />
              </Field>
            ) : null}

            <div style={grid2}>
              <Field
                id="lead-fn"
                label={form.leadKind === 'person' ? 'نام' : 'نام رابط'}
                required={form.leadKind === 'person'}
                error={errors['personFirstName']}
              >
                <input
                  className="sk-input"
                  autoComplete="off"
                  value={form.personFirstName}
                  onChange={(e) => set('personFirstName', e.target.value)}
                  {...inputProps('lead-fn', errors['personFirstName'])}
                />
              </Field>
              <Field
                id="lead-ln"
                label={form.leadKind === 'person' ? 'نام خانوادگی' : 'نام خانوادگی رابط'}
                required={form.leadKind === 'person'}
                error={errors['personLastName']}
              >
                <input
                  className="sk-input"
                  autoComplete="off"
                  value={form.personLastName}
                  onChange={(e) => set('personLastName', e.target.value)}
                  {...inputProps('lead-ln', errors['personLastName'])}
                />
              </Field>
            </div>

            <div style={grid2}>
              <Field
                id="lead-mobile"
                label="شمارهٔ همراه"
                error={errors['mobile']}
                hint="ارقام فارسی یا انگلیسی؛ مثل ۰۹۱۲۳۴۵۶۷۸۹"
              >
                <input
                  className="sk-input"
                  inputMode="tel"
                  dir="ltr"
                  value={form.mobile}
                  onChange={(e) => set('mobile', e.target.value)}
                  onBlur={() => {
                    const normalized = normalizeMobileInput(form.mobile);
                    if (normalized) set('mobile', normalized);
                  }}
                  {...inputProps('lead-mobile', errors['mobile'])}
                  style={{
                    textAlign: 'left',
                    ...inputProps('lead-mobile', errors['mobile']).style,
                  }}
                />
              </Field>
              <Field
                id="lead-phone"
                label="تلفن ثابت"
                error={errors['phone']}
                hint="با کد شهر؛ داخلی اختیاری."
              >
                <input
                  className="sk-input"
                  inputMode="tel"
                  dir="ltr"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  {...inputProps('lead-phone', errors['phone'])}
                  style={{ textAlign: 'left', ...inputProps('lead-phone', errors['phone']).style }}
                />
              </Field>
            </div>

            <div style={grid2}>
              <Field id="lead-alt-kind" label="راه ارتباطی دیگر" error={errors['altContactKind']}>
                <select
                  className="sk-select"
                  value={form.altContactKind}
                  onChange={(e) => set('altContactKind', e.target.value as AltKind)}
                  {...inputProps('lead-alt-kind', errors['altContactKind'])}
                >
                  <option value="">ندارد</option>
                  <option value="email">ایمیل</option>
                  <option value="messenger">پیام‌رسان</option>
                  <option value="other">سایر</option>
                </select>
              </Field>
              {form.altContactKind ? (
                <Field
                  id="lead-alt-value"
                  label={
                    form.altContactKind === 'email'
                      ? 'ایمیل'
                      : form.altContactKind === 'messenger'
                        ? 'شناسه در پیام‌رسان'
                        : 'راه ارتباطی'
                  }
                  error={errors['altContactValue']}
                >
                  <input
                    className="sk-input"
                    dir={form.altContactKind === 'email' ? 'ltr' : undefined}
                    value={form.altContactValue}
                    onChange={(e) => set('altContactValue', e.target.value)}
                    {...inputProps('lead-alt-value', errors['altContactValue'])}
                  />
                </Field>
              ) : null}
            </div>
          </>
        )}

        <Field
          id="lead-need"
          label="موضوع نیاز"
          required
          error={errors['needSubject']}
          hint="مثل «استعلام ERP برای دو شعبه» — در فهرست زیر نام سرنخ نمایش داده می‌شود."
        >
          <input
            className="sk-input"
            value={form.needSubject}
            onChange={(e) => set('needSubject', e.target.value)}
            {...inputProps('lead-need', errors['needSubject'])}
          />
        </Field>

        <div style={grid2}>
          <Field
            id="lead-source"
            label="منبع"
            required={editing !== null || target === 'new'}
            error={errors['sourceLabel']}
          >
            <select
              className="sk-select"
              value={form.sourceLabel}
              onChange={(e) => set('sourceLabel', e.target.value)}
              {...inputProps('lead-source', errors['sourceLabel'])}
            >
              <option value="">انتخاب کنید</option>
              {sources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
              {form.sourceLabel && !sources.includes(form.sourceLabel) ? (
                <option value={form.sourceLabel}>{form.sourceLabel}</option>
              ) : null}
            </select>
          </Field>
          <Field
            id="lead-owner"
            label="مسئول پیگیری"
            error={errors['ownerMemberRef']}
            hint="فقط اعضای فعالی که به سرنخ‌ها دسترسی دارند."
          >
            <select
              className="sk-select"
              value={form.ownerMemberRef}
              disabled={
                options.status !== 'ready' ||
                !options.data.canAssignOwner ||
                ownerDirectory.status === 'loading'
              }
              onChange={(e) => set('ownerMemberRef', e.target.value)}
              {...inputProps('lead-owner', errors['ownerMemberRef'])}
            >
              <option value={editing && selfOwnerRef ? selfOwnerRef : ''}>
                {options.status !== 'ready'
                  ? 'در حال بارگذاری…'
                  : options.data.canAssignOwner
                    ? 'ثبت‌کننده (من)'
                    : 'ثبت‌کننده (من) — تغییر مسئول مجاز نیست'}
              </option>
              {editing?.owner?.memberRef && editing.owner.memberRef !== selfOwnerRef ? (
                <option value={editing.owner.memberRef}>{editing.owner.name}</option>
              ) : null}
              {ownerDirectory.status === 'ready'
                ? ownerDirectory.data.owners
                    .filter(
                      (owner) =>
                        owner.memberRef !== editing?.owner?.memberRef &&
                        owner.memberRef !== selfOwnerRef,
                    )
                    .map((owner) => (
                      <option key={owner.memberRef} value={owner.memberRef}>
                        {owner.displayLabel}
                      </option>
                    ))
                : null}
            </select>
          </Field>
        </div>

        <Field
          id="lead-labels"
          label="برچسب‌ها"
          hint="از برچسب‌های موجود انتخاب کن یا برچسب تازه بساز؛ برای دسته‌بندی و جست‌وجوی سریع."
          error={errors['labelIds']}
        >
          <LabelField
            base={base}
            value={form.labelIds}
            onChange={(labelIds) => set('labelIds', labelIds)}
            canCreate
            {...(editing?.labels ? { initialLabels: editing.labels } : {})}
          />
        </Field>

        {needsDetail || form.sourceLabel === 'معرفی' ? (
          <Field
            id="lead-source-detail"
            label={form.sourceLabel === 'معرفی' ? 'نام معرف' : 'توضیح منبع'}
            required={needsDetail}
            error={errors['sourceDetail']}
          >
            <input
              className="sk-input"
              value={form.sourceDetail}
              onChange={(e) => set('sourceDetail', e.target.value)}
              {...inputProps('lead-source-detail', errors['sourceDetail'])}
            />
          </Field>
        ) : null}

        <fieldset
          style={{
            border: '1px solid var(--sk-border)',
            borderRadius: 'var(--sk-r-md)',
            margin: 0,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <legend style={{ fontSize: 13, fontWeight: 600, padding: '0 6px' }}>معامله</legend>
          <div style={grid2}>
            {!editing ? (
              <Field id="lead-stage" label="مرحلهٔ شروع" error={errors['stageRef']}>
                <select
                  className="sk-select"
                  value={form.stageRef}
                  disabled={options.status !== 'ready'}
                  onChange={(e) => set('stageRef', e.target.value)}
                  {...inputProps('lead-stage', errors['stageRef'])}
                >
                  {options.status === 'ready' ? (
                    (
                      options.data.pipelines.find((p) => p.isDefault) ?? options.data.pipelines[0]
                    )?.stages.map((stage) => (
                      <option key={stage.ref} value={stage.ref}>
                        {stage.name} · احتمال {stage.probabilityPct.toLocaleString('fa-IR')}٪
                      </option>
                    ))
                  ) : (
                    <option value="">در حال بارگذاری…</option>
                  )}
                </select>
              </Field>
            ) : null}
            <Field
              id="lead-amount"
              label={`مبلغ برآوردی (${unitLabel === 'ریال' ? 'ریال' : 'ریال — نمایش به ' + unitLabel})`}
              error={errors['amountRial']}
              hint="خالی یعنی هنوز برآورد نشده."
            >
              <input
                className="sk-input"
                inputMode="numeric"
                dir="ltr"
                value={form.amountRial ? formatRialFa(form.amountRial, 'rial') : ''}
                onChange={(e) => set('amountRial', parseRialInput(e.target.value))}
                {...inputProps('lead-amount', errors['amountRial'])}
              />
            </Field>
            <Field id="lead-close" label="تاریخ احتمالی بستن" error={errors['expectedCloseDate']}>
              <JalaliDateInput
                value={form.expectedCloseDate}
                onChange={(value) => set('expectedCloseDate', value)}
              />
            </Field>
            <Field id="lead-priority" label="اولویت">
              <select
                className="sk-select"
                value={form.priority}
                onChange={(e) => set('priority', e.target.value as LeadPriority)}
              >
                {(options.status === 'ready'
                  ? options.data.priorities
                  : [{ value: 'normal' as const, label: 'عادی' }]
                ).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field id="lead-need-desc" label="شرح نیاز" hint="جزئیات نیاز مشتری برای تیم فروش.">
            <textarea
              id="lead-need-desc"
              className="sk-textarea"
              rows={2}
              value={form.needDescription}
              onChange={(e) => set('needDescription', e.target.value)}
            />
          </Field>
          <div style={grid2}>
            <Field id="lead-next-title" label="گام بعدی" error={errors['nextActionTitle']}>
              <input
                className="sk-input"
                placeholder="مثلاً ارسال پیشنهاد قیمت"
                value={form.nextActionTitle}
                onChange={(e) => set('nextActionTitle', e.target.value)}
                {...inputProps('lead-next-title', errors['nextActionTitle'])}
              />
            </Field>
            <Field id="lead-next-due" label="مهلت گام بعدی" error={errors['nextActionDueAt']}>
              <JalaliDateTimeInput
                value={form.nextActionDueAt}
                onChange={(value) => set('nextActionDueAt', value)}
              />
            </Field>
          </div>
          <Field
            id="lead-products"
            label="محصولات مورد نظر"
            error={errors['productRefs']}
            hint={
              productList.status === 'error'
                ? 'فهرست محصولات برای شما قابل دسترسی نیست.'
                : 'از فهرست محصولات فعال انتخاب کنید.'
            }
          >
            {form.products.length > 0 ? (
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.products.map((product) => (
                  <span
                    key={product.ref}
                    className="sk-tag"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {product.name}
                    <button
                      type="button"
                      aria-label={`حذف ${product.name}`}
                      onClick={() =>
                        set(
                          'products',
                          form.products.filter((item) => item.ref !== product.ref),
                        )
                      }
                      style={{
                        border: 0,
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: 'inherit',
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </span>
            ) : null}
            <input
              id="lead-products"
              className="sk-input"
              placeholder="جست‌وجوی محصول…"
              value={productSearch}
              disabled={productList.status === 'error'}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            {productList.status === 'ready' && productSearch.trim() ? (
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {productList.data.items
                  .filter((item) => !form.products.some((p) => p.ref === String(item['code'])))
                  .slice(0, 8)
                  .map((item) => (
                    <button
                      key={String(item['code'])}
                      type="button"
                      className="sk-btn sk-btn--secondary sk-btn--sm"
                      onClick={() => {
                        set('products', [
                          ...form.products,
                          { ref: String(item['code']), name: String(item['name'] ?? item['code']) },
                        ]);
                        setProductSearch('');
                      }}
                    >
                      + {String(item['name'] ?? item['code'])}
                    </button>
                  ))}
              </span>
            ) : null}
          </Field>
        </fieldset>

        {editing ? null : (
          <>
            <Field
              id="lead-followup"
              label="پیگیری بعدی"
              error={errors['nextFollowUpAt']}
              hint="اختیاری — به‌وقت تهران. یک فعالیت تماس برای مسئول پیگیری ساخته می‌شود."
            >
              <JalaliDateTimeInput
                value={form.nextFollowUp}
                onChange={(value) => set('nextFollowUp', value)}
              />
              {form.nextFollowUp && wallClockToIso(form.nextFollowUp) ? (
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  {relativeWhen(wallClockToIso(form.nextFollowUp) as string)}
                </span>
              ) : null}
            </Field>
            <Field id="lead-note" label="یادداشت اولیه" hint="اختیاری — در سابقهٔ سرنخ ثبت می‌شود.">
              <textarea
                id="lead-note"
                className="sk-textarea"
                rows={3}
                value={form.initialNote}
                onChange={(e) => set('initialNote', e.target.value)}
              />
            </Field>
          </>
        )}

        {duplicate ? (
          <div
            role="alert"
            style={{
              display: 'flex',
              gap: 10,
              padding: 12,
              borderRadius: 'var(--sk-r-md)',
              background: 'var(--sk-warn-soft)',
              border: '1px solid color-mix(in oklch, var(--sk-warn), transparent 78%)',
            }}
          >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12, lineHeight: 1.7, color: 'var(--sk-fg-muted)' }}>
                <b style={{ color: 'var(--sk-fg)' }}>سرنخ مشابه یافت شد.</b> «{duplicate.title}» با
                همین راه ارتباطی{' '}
                {duplicate.at ? relativeWhen(duplicate.at, { withTime: false }) : ''} ثبت شده است.
              </span>
              <button
                type="button"
                className="sk-btn sk-btn--secondary sk-btn--sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => onOpenLead(duplicate.ref)}
              >
                مشاهدهٔ سرنخ موجود
              </button>
            </div>
          </div>
        ) : null}
        <button type="submit" hidden />
      </form>
    </SidePanel>
  );
}
